import { d as debounce } from './debounce-CFN_LA0O.js';

class AnnouncementBar extends HTMLElement {
  #timeout = null;
  #lastScrollPosition = window.scrollY;
  #barHeight = 0;
  #stickyBehavior = '';
  #hasScrollIndicator = false;
  #scrollHandler = null;
  /** @type {HTMLElement | null} */ #section = null;
  /** @type {HTMLElement | null} */ #surface = null;

  #resizeObserver = null;
  /** @type {IntersectionObserver | null} */ #inViewObserver = null;
  #inView = false;
  #inViewHeight = 0;
  #nonStickyResizeHandler = null;
  /** @type {number | null} */ #appliedHeight = null;
  /** @type {number | null} */ #appliedTop = null;

  connectedCallback() {
    if (!this.hasAttribute('data-menu-clone')) {
      this.#stickyBehavior = this.dataset.stickyBehavior || '';
      this.#hasScrollIndicator = this.dataset.hasScrollIndicator === 'true';
      this.#section = this.closest('.shopify-section--announcement-bar');
      this.#surface = this.querySelector('.announcement-bar__surface');

      if (this.#usesStickySection()) {
        this.#barHeight = this.#surface?.getBoundingClientRect().height ?? 0;
      }

      this.#injectIntoMenuDrawer();
      this.#promoteStickyToSection();
      this.#initStickyBehavior();
      this.#initNonStickyHeightTracking();
    }

    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    clearTimeout(this.#timeout);

    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
    }

    if (this.#nonStickyResizeHandler) {
      window.removeEventListener('resize', this.#nonStickyResizeHandler);
    }

    window.removeEventListener('resize', this.#measureHeight);
    this.#resizeObserver?.disconnect();
    this.#inViewObserver?.disconnect();

    if (this.#usesStickySection()) {
      document.documentElement.style.removeProperty('--announcement-bar-top');
      this.#appliedTop = null;
    }
  }

  #usesStickySection() {
    return Boolean(this.#stickyBehavior) || this.#hasScrollIndicator;
  }

  /** Semi-sticky, or not-sticky with an indicator (indicator must stay pinned). */
  #usesCollapse() {
    return (
      this.#stickyBehavior === 'semi-sticky' || (this.#hasScrollIndicator && !this.#stickyBehavior)
    );
  }

  #promoteStickyToSection() {
    if (!this.#section || !this.#usesStickySection()) {
      return;
    }

    this.#section.classList.add('announcement-bar-section--sticky', 'sticky', 'top-0', 'w-full');

    if (this.#usesCollapse()) {
      this.#section.classList.add('announcement-bar-section--semi-sticky');
    }

    this.#syncHeaderStickyOffset();
  }

  #syncHeaderStickyOffset(attempts = 0) {
    const headerSection = document.querySelector('.shopify-section--header');

    if (!headerSection) {
      if (attempts < 20) {
        requestAnimationFrame(() => this.#syncHeaderStickyOffset(attempts + 1));
      }

      return;
    }

    headerSection.classList.remove(
      'top-0',
      'top-below-announcement-bar',
      'top-below-scroll-indicator'
    );
    // Utility layer beats the announcement-bar.css :has() rule, so pick the
    // offset that includes --scroll-indicator-height when an indicator exists.
    headerSection.classList.add(
      this.#hasScrollIndicator ? 'top-below-scroll-indicator' : 'top-below-announcement-bar'
    );
  }

  /**
   * Non-sticky bars (no indicator): expose height only while the bar is in view.
   */
  #initNonStickyHeightTracking() {
    if (this.#usesStickySection()) {
      return;
    }

    this.#inView = this.#isInView();
    this.#updateNonStickyEffectiveHeight();

    this.#inViewObserver = new IntersectionObserver(
      ([entry]) => {
        this.#inView = entry.isIntersecting;
        this.#updateNonStickyEffectiveHeight();
      },
      { threshold: 0 }
    );
    this.#inViewObserver.observe(this);

    this.#nonStickyResizeHandler = debounce(() => {
      this.#updateNonStickyEffectiveHeight();
    }, 100);
    window.addEventListener('resize', this.#nonStickyResizeHandler, { passive: true });
  }

  #isInView() {
    const { bottom } = this.getBoundingClientRect();
    return bottom > 0;
  }

  #updateNonStickyEffectiveHeight() {
    if (this.#usesStickySection()) {
      return;
    }

    if (this.#inView) {
      this.#inViewHeight = this.getBoundingClientRect().height;
    }

    const height = this.#inView ? this.#inViewHeight : 0;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }

  #observeSurface() {
    if (!this.#surface) {
      this.#measureHeight();
      return;
    }

    this.#resizeObserver = new ResizeObserver(() => {
      this.#measureHeight();
    });
    this.#resizeObserver.observe(this.#surface);
  }

  #initStickyBehavior() {
    if (!this.#usesStickySection()) {
      return;
    }

    this.#observeSurface();
    this.#setEffectiveHeight(this.#barHeight);

    if (this.#usesCollapse() && this.#barHeight > 0) {
      this.#syncScrollPosition();
    }

    requestAnimationFrame(() => this.#measureHeight());
    window.addEventListener('load', this.#measureHeight, { once: true });

    window.addEventListener('resize', this.#measureHeight, { passive: true });
    document.fonts?.ready?.then(() => this.#measureHeight());

    if (this.#usesCollapse()) {
      this.#scrollHandler = this.#handleScroll;
      window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    }

    this.#syncHeaderStickyOffset();
  }

  #measureHeight = () => {
    const height = this.#surface?.getBoundingClientRect().height ?? 0;

    this.#barHeight = height;

    if (this.#usesStickySection()) {
      this.#setEffectiveHeight(height);
    }

    if (this.#usesCollapse() && height > 0) {
      this.#syncScrollPosition();
    }
  };

  /** Set collapse state from scroll position (used on load — not scroll direction). */
  #syncScrollPosition() {
    const scrollY = window.scrollY;
    this.#lastScrollPosition = scrollY;

    if (!this.#stickyBehavior) {
      const hide = scrollY > this.#barHeight;
      this.#updateBarTop(hide ? this.#barHeight : 0);
      this.#setScrollState({ scrolled: hide, scrollUp: false });
      return;
    }

    if (scrollY <= this.#barHeight) {
      this.#updateBarTop(0);
      this.#setScrollState({ scrolled: false, scrollUp: false });
      return;
    }

    this.#updateBarTop(this.#barHeight);
    this.#setScrollState({ scrolled: true, scrollUp: false });
  }

  #updateBarTop = (top = 0) => {
    if (this.#appliedTop === top) return;

    this.#appliedTop = top;
    document.documentElement.style.setProperty('--announcement-bar-top', `${top}px`);
  };

  #setEffectiveHeight = (height) => {
    if (this.#appliedHeight === height) return;

    this.#appliedHeight = height;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  };

  #setScrollState = ({ scrolled, scrollUp }) => {
    this.classList.toggle('scrolled', scrolled);
    this.classList.toggle('scroll-up', scrollUp);
    this.#section?.classList.toggle('scrolled', scrolled);
    this.#section?.classList.toggle('scroll-up', scrollUp);
  };

  #handleScroll = () => {
    if (!this.#usesCollapse()) {
      return;
    }

    // Not sticky + indicator: hide announcement once scrolled past it.
    if (!this.#stickyBehavior) {
      const hide = window.scrollY > this.#barHeight;
      this.#updateBarTop(hide ? this.#barHeight : 0);
      this.#setScrollState({ scrolled: hide, scrollUp: false });
      return;
    }

    // Semi-sticky: hide on scroll down, restore on scroll up.
    const currentScrollPosition = window.scrollY;

    if (currentScrollPosition <= this.#barHeight) {
      this.#updateBarTop();
      this.#lastScrollPosition = currentScrollPosition;
      this.#setScrollState({ scrolled: false, scrollUp: false });
      return;
    }

    const isScrollingDown = currentScrollPosition > this.#lastScrollPosition;
    const scrollUp = !isScrollingDown;

    if (scrollUp) {
      this.#updateBarTop();
    } else {
      this.#updateBarTop(this.#barHeight);
    }

    this.#setScrollState({ scrolled: true, scrollUp });
    this.#lastScrollPosition = currentScrollPosition;
  };

  #injectIntoMenuDrawer() {
    const drawerContent = document
      .getElementById('menu-drawer')
      ?.querySelector('[data-body] .drawer__content');
    if (!drawerContent) return;

    const clone = this.cloneNode(true);
    clone.setAttribute('data-menu-clone', '');
    clone.classList.add('announcement-bar--menu');
    clone.dataset.hasScrollIndicator = 'false';
    clone.querySelector('scroll-indicator')?.remove();
    drawerContent.before(clone);
  }

  #handleClick = async (event) => {
    const btn = event.target.closest('[data-code]');
    if (!btn) return;

    await navigator.clipboard.writeText(btn.dataset.code);
    this.#setCopied(btn);
  };

  #setCopied(btn) {
    if (btn.classList.contains('is-copied')) return;

    const label = btn.querySelector('.announcement-bar__copy-label');
    btn.classList.add('is-copied', 'is-rippling');
    if (label) label.textContent = btn.dataset.copiedText || 'Copied!';

    clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      btn.classList.remove('is-copied', 'is-rippling');
      if (label) label.textContent = btn.dataset.copyText || 'Copy';
    }, 2000);
  }
}

customElements.define('announcement-bar', AnnouncementBar);
